import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LangSwitcher } from "@/components/LangSwitcher";
import { SeoHead } from "@/components/SeoHead";

const Shell = ({ title, updated, children }) => (
  <div className="min-h-screen bg-[#FAFAFA]">
    <header className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
      <Link to="/" className="font-heading font-extrabold text-xl tracking-tight">depoia<span className="text-[#FF4F00]">.</span></Link>
      <LangSwitcher />
    </header>
    <main className="max-w-3xl mx-auto px-6 pb-24">
      <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight mt-6">{title}</h1>
      <p className="text-xs text-zinc-400 mt-2">{updated}</p>
      <div className="mt-8 space-y-6 text-sm text-zinc-700 leading-relaxed [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-lg [&_h2]:text-zinc-900 [&_h2]:mt-8 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
        {children}
      </div>
    </main>
  </div>
);

export function Privacy() {
  const { i18n, t } = useTranslation();
  const en = !i18n.language?.startsWith("pt");
  return (
    <Shell title={t("legal.privacyTitle")} updated={t("legal.lastUpdate")}>
      <SeoHead pageKey="privacy" />
      {en ? (
        <>
          <p>This Privacy Policy describes how the depoia platform ("Platform") handles personal data, aligned with GDPR principles and applicable data protection laws.</p>
          <h2>1. Roles</h2>
          <p>Regarding data from <strong>testimonial senders</strong>, the <strong>account owner</strong> (the business that created the campaign) acts as <strong>data controller</strong>. The Platform acts as <strong>data processor</strong>. For account owners' own data, the Platform is the controller.</p>
          <h2>2. Data collected</h2>
          <ul>
            <li><strong>From senders:</strong> name, email, role/company (optional), rating, testimonial content (text, video or voice), AI transcript, and consent record (timestamp).</li>
            <li><strong>From owners:</strong> name, email and picture from Google sign-in, campaigns and payment metadata processed by Stripe (we don't store card data).</li>
          </ul>
          <h2>3. Purpose</h2>
          <p>Sender data is processed exclusively to (i) collect, store and display testimonials on the business's public wall and marketing materials, subject to consent given at submission; (ii) allow the owner to manage, approve or delete testimonials; and (iii) generate transcripts and highlights. Senders' emails <strong>are never displayed publicly</strong>.</p>
          <h2>4. Legal basis and consent</h2>
          <p>Every submission requires explicit consent, recorded with timestamp, authorizing use of the testimonial on the business's site and marketing materials.</p>
          <h2>5. Sharing</h2>
          <p>Approved testimonials are public on the wall embedded in the business's site. We use infrastructure providers (file storage, payments, AI transcription) strictly to operate the Platform.</p>
          <h2>6. Data subject rights</h2>
          <p>You may request confirmation of processing, access, correction and <strong>deletion</strong> of your data at any time. Contact the business the testimonial was sent to (controller), who can permanently delete the testimonial via the dashboard — including video/audio removal from storage. The Platform helps controllers fulfill these requests.</p>
          <h2>7. Retention and security</h2>
          <p>Data is retained while the account and testimonial exist, or until deletion is requested. We use reasonable technical and organizational safeguards including encrypted transmission (HTTPS) and session-based access control.</p>
          <h2>8. Contact</h2>
          <p>For questions or to exercise your rights, contact the business responsible for the campaign or Platform support.</p>
        </>
      ) : (
        <>
          <p>Esta Política de Privacidade descreve como a plataforma depoia ("Plataforma") trata dados pessoais, em conformidade com a Lei Geral de Proteção de Dados Pessoais — Lei nº 13.709/2018 ("LGPD").</p>
          <h2>1. Papéis no tratamento de dados</h2>
          <p>Em relação aos dados dos <strong>depoentes</strong>, o <strong>dono da conta</strong> atua como <strong>controlador</strong>. A Plataforma atua como <strong>operadora</strong>. Para os dados dos donos, a Plataforma é controladora.</p>
          <h2>2. Dados coletados</h2>
          <ul>
            <li><strong>Dos depoentes:</strong> nome, email, cargo/empresa (opcional), nota, conteúdo em texto/vídeo/voz, transcrição gerada e registro de consentimento.</li>
            <li><strong>Dos donos:</strong> nome, email e foto do login Google, campanhas e metadados de pagamento pelo Stripe (não armazenamos dados de cartão).</li>
          </ul>
          <h2>3. Finalidade</h2>
          <p>Coletar, armazenar e exibir o depoimento no mural público e em materiais de divulgação; permitir gestão pelo dono; gerar transcrições e resumos. O email do depoente <strong>nunca é exibido publicamente</strong>.</p>
          <h2>4. Base legal e consentimento</h2>
          <p>Consentimento expresso (art. 7º, I, da LGPD) registrado com data/hora autorizando o uso do depoimento.</p>
          <h2>5. Compartilhamento</h2>
          <p>Depoimentos aprovados ficam públicos no mural. Usamos fornecedores (armazenamento, pagamentos, IA) estritamente para operar a Plataforma.</p>
          <h2>6. Direitos dos titulares</h2>
          <p>Nos termos do art. 18 da LGPD, o titular pode solicitar acesso, correção e <strong>exclusão</strong>. Dirija-se ao negócio responsável (controlador), que pode excluir em definitivo pelo painel — incluindo remoção do vídeo/áudio do storage.</p>
          <h2>7. Retenção e segurança</h2>
          <p>Dados mantidos enquanto a conta/depoimento existirem, ou até solicitação de exclusão. HTTPS + controle de acesso por sessão.</p>
          <h2>8. Contato</h2>
          <p>Para dúvidas, contate o negócio responsável pela campanha ou o suporte da Plataforma.</p>
        </>
      )}
      <div className="mt-12 flex gap-4 text-sm">
        <Link to="/termos" className="text-[#FF4F00] font-semibold hover:underline">{t("legal.seeTerms")}</Link>
      </div>
    </Shell>
  );
}

export function Terms() {
  const { i18n, t } = useTranslation();
  const en = !i18n.language?.startsWith("pt");
  return (
    <Shell title={t("legal.termsTitle")} updated={t("legal.lastUpdate")}>
      <SeoHead pageKey="terms" />
      {en ? (
        <>
          <p>By using the depoia platform ("Platform"), you agree to these Terms of Service.</p>
          <h2>1. The service</h2>
          <p>The Platform lets businesses ("owners") collect testimonials from customers ("senders") via public link, in video, voice or text, and display them on an embeddable wall.</p>
          <h2>2. Owner responsibilities</h2>
          <ul>
            <li>Act as data controller for senders' personal data.</li>
            <li>Use testimonials strictly within the authorization granted by the sender.</li>
            <li>Fulfill deletion requests from senders using the permanent-delete feature.</li>
            <li>Do not use the Platform for illegal, defamatory or third-party-infringing content.</li>
          </ul>
          <h2>3. Sender consent</h2>
          <p>Every testimonial requires explicit sender authorization to be used on the business's site and marketing. The consent record is stored with the testimonial.</p>
          <h2>4. Plans and payments</h2>
          <p>The Free plan has usage limits. The Pro plan is a Stripe subscription (monthly or yearly) that can be canceled anytime and remains active until the end of the paid period.</p>
          <h2>5. Content ownership</h2>
          <p>Testimonials belong to their authors, who license usage to the business per the given consent. The Platform claims no ownership over the content.</p>
          <h2>6. Limitation of liability</h2>
          <p>The Platform is provided "as is". We are not liable for testimonial content or how owners use it outside the granted authorizations.</p>
          <h2>7. Termination</h2>
          <p>We may suspend accounts violating these terms. Owners can close their account anytime; deleting campaigns removes associated testimonials.</p>
          <h2>8. Changes</h2>
          <p>These terms may be updated; relevant changes will be communicated on the Platform.</p>
        </>
      ) : (
        <>
          <p>Ao usar a plataforma depoia ("Plataforma"), você concorda com estes Termos de Uso.</p>
          <h2>1. O serviço</h2>
          <p>A Plataforma permite que negócios ("donos") coletem depoimentos de clientes ("depoentes") por link público em vídeo, voz ou texto, e exibam em um mural incorporável.</p>
          <h2>2. Responsabilidades do dono</h2>
          <ul>
            <li>Atuar como <strong>controlador</strong> dos dados pessoais dos depoentes.</li>
            <li>Usar os depoimentos apenas conforme a autorização concedida.</li>
            <li>Atender solicitações de exclusão usando a exclusão definitiva do painel.</li>
            <li>Não usar a Plataforma para conteúdo ilegal, difamatório ou que viole direitos de terceiros.</li>
          </ul>
          <h2>3. Consentimento</h2>
          <p>Todo depoimento requer autorização expressa do depoente. O registro fica armazenado junto ao depoimento.</p>
          <h2>4. Planos e pagamento</h2>
          <p>O Grátis tem limites. O Pro é assinatura Stripe (mensal ou anual), cancelável a qualquer momento, ativo até o fim do período pago.</p>
          <h2>5. Propriedade</h2>
          <p>Depoimentos pertencem aos autores, que licenciam o uso ao negócio conforme consentimento. A Plataforma não reivindica propriedade.</p>
          <h2>6. Limitação de responsabilidade</h2>
          <p>A Plataforma é fornecida "como está". Não nos responsabilizamos pelo conteúdo dos depoimentos nem pelo uso fora das autorizações.</p>
          <h2>7. Encerramento</h2>
          <p>Podemos suspender contas que violem estes termos. O dono pode encerrar a conta a qualquer momento; exclusão de campanhas remove depoimentos associados.</p>
          <h2>8. Alterações</h2>
          <p>Estes termos podem ser atualizados; alterações relevantes serão comunicadas na Plataforma.</p>
        </>
      )}
      <div className="mt-12 flex gap-4 text-sm">
        <Link to="/privacidade" className="text-[#FF4F00] font-semibold hover:underline">{t("legal.seePrivacy")}</Link>
      </div>
    </Shell>
  );
}
